<template>
  <div class="coupon-history-tab">
    <!-- 新增票券區塊（補發）：僅 R7 可見 -->
    <section v-if="canCreateCouponRecord" class="mb-[20px] py-[30px] px-[24px] bg-white rounded">
      <BaseElForm
        ref="addCouponForm"
        :model="formData"
        :rules="formRules"
        label-width="120px"
        label-position="left"
      >
        <BaseElFormItem :label="$t('member.tab.couponHistory.selectCoupon')" prop="couponId">
          <BaseElSelect
            v-model="formData.couponId"
            :placeholder="$t('member.tab.couponHistory.clickToSelect')"
            popper-class="select-popper"
            @visible-change="showCouponSelectModal = true"
          >
            <BaseElSelectOption
              v-if="formData.couponId"
              :value="formData.couponId"
              :label="findCouponName(formData.couponId)"
            />
          </BaseElSelect>
        </BaseElFormItem>

        <BaseElFormItem :label="$t('member.tab.couponHistory.messageLabel')">
          <BaseElInput
            v-model="formData.message"
            type="textarea"
            :placeholder="$t('member.tab.couponHistory.messagePlaceholder')"
            :autosize="{ minRows: 5, maxRows: 5 }"
            maxlength="1000"
            show-word-limit
          />
        </BaseElFormItem>

        <BaseElFormItem>
          <BaseElButton plain @click="clearForm">{{ $t('member.tab.couponHistory.clear') }}</BaseElButton>
          <BaseElButton
            type="primary"
            :loading="loading.addCoupon || loading.checkCouponNotify"
            @click="createCouponRecord"
          >
            {{ $t('member.tab.couponHistory.addCoupon') }}
          </BaseElButton>
        </BaseElFormItem>
      </BaseElForm>
    </section>
    <BobProPage
      ref="bobProPageRef"
      :columns="tableColumns"
      :tableConfig="tableConfig"
    >
      <!-- 自訂篩選器以包含選項 -->
      <template #filters="{ filters, refresh }">
        <FiltersContainer>
          <BaseElSelect
            v-model="filters.status"
            clearable
            :placeholder="$t('member.tab.couponHistory.usageStatus')"
            @change="refresh(true)"
          >
            <BaseElSelectOption
              v-for="option in couponStatusConfig"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </BaseElSelect>

          <BaseElSelect
            v-model="filters.orderBy"
            clearable
            :placeholder="$t('member.tab.couponHistory.sortBy')"
            @change="refresh(true)"
          >
            <BaseElSelectOption
              v-for="option in orderByOptionsConfig"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </BaseElSelect>
        </FiltersContainer>
      </template>
    </BobProPage>

    <WarningDialog
      v-if="showWarningDialog"
      :confirmBtnLoading="loading.addCoupon"
      @close="showWarningDialog = false"
      @confirm="createCouponRecord($event, true)"
    >
      <template #body>
        <p v-for="warning in couponWarnings" :key="warning.code">{{ warning.message }}</p>
      </template>
    </WarningDialog>

    <CouponAdvanceSelect
      v-if="showCouponSelectModal"
      :data="couponList"
      :typeOptions="couponTypeOptions"
      :selectedData="formData.couponId ? [formData.couponId] : []"
      :disabledExp="true"
      @confirm="onAddCoupon"
      @close="showCouponSelectModal = false"
    />

    <WarningDialog
      v-if="showUseCouponDialog"
      :content="$t('member.tab.couponHistory.useConfirm')"
      :confirmBtnLoading="loading.useCoupon"
      @close="showUseCouponDialog = false"
      @confirm="useCoupon"
    />
    <WarningDialog
      v-if="voidDialog"
      :title="$t('member.tab.couponHistory.voidWarningTitle')"
      width="600px"
      :confirmBtnLoading="loading.voidCoupon"
      @close="voidDialog = false"
      @confirm="voidCouponRecord"
    >
      <template #body>
        <p>{{ $t('member.tab.couponHistory.voidWarningContent') }}</p>
        <p class="text-danger">{{ $t('member.tab.couponHistory.voidWarningNote') }}</p>
      </template>
    </WarningDialog>
  </div>
</template>

<script>
import BobProPage from '@/components/BobProPage.vue'
import WarningDialog from '@/components/Dialog/WarningDialog.vue'
import CouponAdvanceSelect from '@/components/Select/CouponAdvanceSelect.vue'
import OperateColumn from '@/components/OperateColumn.vue'
import Tag from '@/components/Tag/Tag.vue'
import { GetCouponRecordCreateCheck } from '@/api/coupon'
import {
  CreateCouponRecord,
  GetCouponRecord,
  GetCouponRecordCount,
  MarkVoidCouponRecord,
  UseCoupon,
} from '@/api/lottery/couponRecord'
import { noEmptyRules } from '@/validation/index'
import formUtils from '@/utils/form'
import { get, omit, find, includes } from 'lodash'
import { couponStatusConfig, orderByOptionsConfig, couponOriginTypeConfig, couponTypeConfig } from '@/config/coupon'
import { defineComponent, ref, computed, reactive, onMounted } from 'vue'
import { useBobProPage } from '@/use/useBobProPage'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'
import { useCoupon as useCouponComposable } from '@/use/useCoupon'
import { useOrgBenefitControl } from '@/use/useOrgBenefitControl'
import { i18n } from '@/plugins/i18n/i18n'
import { formatDate } from '@/utils/date'
import { canManuallyRedeemCyjPosCoupon } from '@/utils/cyjPosCoupon'
import { getCouponSyncStatusDisplay } from '@/config/couponSyncStatus'

export default defineComponent({
  name: 'CouponHistoryTab',
  components: {
    BobProPage,
    WarningDialog,
    CouponAdvanceSelect,
  },
  props: ['memberId'],
  setup (props) {
    const { shopId } = useShop()
    const { checkAction } = usePermissions()
    const { couponFeatures, disabledCouponTypes, getAllCouponResources, composeAllCouponResources } = useCouponComposable()
    const { isCouponBenefitSharingEnabled } = useOrgBenefitControl()
    const { bobProPageRef, onRefresh } = useBobProPage()
    const canUseCouponRecord = computed(() => checkAction('admin.couponRecord.use'))
    const canCreateCouponRecord = computed(() => checkAction('admin.couponRecord.create'))

    // 票券列表
    const couponList = ref([])
    const showCouponSelectModal = ref(false)
    const showWarningDialog = ref(false)
    const showUseCouponDialog = ref(false)
    const voidDialog = ref(false)
    const addCouponForm = ref(null)
    const couponWarnings = ref([])
    const selectCoupon = ref(null)
    const selectVoidRow = ref(null)
    const loading = reactive({
      addCoupon: false,
      useCoupon: false,
      voidCoupon: false,
      checkCouponNotify: false,
    })

    // 根據權限過濾票券類型選項
    const couponTypeOptions = computed(() => {
      return omit(couponTypeConfig, disabledCouponTypes.value)
    })

    // 新增票券表單資料
    const formData = reactive({
      couponId: null,
      message: '',
    })

    const formRules = {
      couponId: [noEmptyRules()],
    }

    const couponNotify = computed(() => {
      // 無警告訊息表示可以發送通知
      return couponWarnings.value.length === 0
    })

    // 工具函式
    const getCouponRecordType = (row) => row.Coupon?.type || row.type

    const isThirdPartyCoupon = (type) => {
      return includes(['pospal', 'tkhspc', 'opentix', 'cyjPos'], type)
    }

    const canVoidCoupon = (type, status) => {
      return isThirdPartyCoupon(type) && status === 'open'
    }

    const hideUseCoupon = (type) => {
      return includes(['pospal', 'tkhspc'], type)
    }

    const disableUseCoupon = (row) => {
      const type = getCouponRecordType(row)
      const cannotRedeemCyjPosCoupon = type === 'cyjPos' && !canManuallyRedeemCyjPosCoupon(row)

      return cannotRedeemCyjPosCoupon || includes(['complete', 'overdue', 'voided', 'markedVoided'], row.status)
    }

    const couponType = (type) => {
      const config = get(couponTypeConfig, `${type}`, null)
      return config ? config.label : '-'
    }

    const transOriginType = (originType) => {
      return get(couponOriginTypeConfig, `${originType}.label`, '-')
    }

    // 表格欄位配置
    const tableColumns = computed(() => [
      {
        prop: 'originType',
        label: i18n.t('member.tab.couponHistory.origin'),
        component: 'div',
        render: (row) => transOriginType(row.originType),
        align: 'center',
      },
      {
        prop: 'code',
        label: i18n.t('member.tab.couponHistory.couponCode'),
        component: 'div',
        render: (row) => row.code,
        align: 'center',
      },
      {
        prop: 'couponType',
        label: i18n.t('member.tab.couponHistory.type'),
        component: 'div',
        render: (row) => couponType(getCouponRecordType(row)),
        align: 'center',
      },
      {
        prop: 'Coupon.name',
        label: i18n.t('member.tab.couponHistory.name'),
        component: 'div',
        render: (row) => row.Coupon?.name || '-',
        align: 'center',
      },
      {
        prop: 'exchangeCode',
        label: i18n.t('member.tab.couponHistory.exchangeCode'),
        component: 'div',
        render: (row) => {
          const type = getCouponRecordType(row)
          const thirdPartyTypes = ['pospal', 'opentix', 'tkhspc']
          if (thirdPartyTypes.includes(type)) {
            return row.thirdPartyCode || '-'
          }
          return row.exchangeCode || '-'
        },
        align: 'center',
      },
      ...(checkAction('planModule.cyjPos.enable') ? [{
        prop: 'thirdPartyCode',
        label: 'CYJ 凱惠優惠券核銷序號',
        component: 'div',
        render: (row) => getCouponRecordType(row) === 'cyjPos' ? (row.thirdPartyCode || '尚未取得') : '-',
        align: 'center',
        columnAttrs: {
          'show-overflow-tooltip': true,
        },
      }] : []),
      {
        prop: 'status',
        label: i18n.t('member.tab.couponHistory.usageStatusCol'),
        component: Tag,
        render: (row) => couponStatusConfig[row.status].label,
        props: (row) => ({
          type: couponStatusConfig[row.status].tagType,
        }),
        align: 'center',
      },
      {
        prop: 'syncStatus',
        label: '同步狀態',
        component: () => import('@/components/Tag/StatusDisplay.vue'),
        props: (row) => {
          const { label, tagType, isTag } = getCouponSyncStatusDisplay(row)
          return {
            displayText: label,
            tagType,
            isTag,
          }
        },
        align: 'center',
      },
      {
        prop: 'createdAt',
        label: i18n.t('member.tab.couponHistory.sendDate'),
        component: 'div',
        render: (row) => row.createdAt ? formatDate(row.createdAt) : '-',
        align: 'center',
      },
      {
        prop: 'usedAt',
        label: i18n.t('member.tab.couponHistory.useDate'),
        component: 'div',
        render: (row) => row.usedAt ? formatDate(row.usedAt) : '-',
        align: 'center',
      },
      {
        prop: 'expAt',
        label: i18n.t('member.tab.couponHistory.expiryDate'),
        component: 'div',
        render: (row) => row.expAt ? formatDate(row.expAt) : '-',
        align: 'center',
      },
      {
        prop: 'voidAt',
        label: i18n.t('member.tab.couponHistory.voidTime'),
        component: 'div',
        render: (row) => {
          if (['voided', 'markedVoided'].includes(row.status)) {
            return row.voidAt ? formatDate(row.voidAt) : '-'
          }
          return '-'
        },
        align: 'center',
      },
      {
        prop: 'originName',
        label: i18n.t('member.tab.couponHistory.remark'),
        component: 'div',
        render: (row) => row.originName || '-',
        align: 'center',
        columnAttrs: {
          'show-overflow-tooltip': true,
        },
      },
      ...(isCouponBenefitSharingEnabled.value ? [{
        prop: 'Shop',
        label: i18n.t('member.tab.couponHistory.issuingBranch'),
        component: 'div',
        render: (row) => row.Shop?.name || '-',
        align: 'center',
      }, {
        prop: 'GrantedShop',
        label: i18n.t('member.tab.couponHistory.grantBranch'),
        component: 'div',
        render: (row) => row.GrantedShop?.name || '-',
        align: 'center',
      }, {
        prop: 'RedemptionShop',
        label: i18n.t('member.tab.couponHistory.redeemBranch'),
        component: 'div',
        render: (row) => row.RedemptionShop?.name || '-',
        align: 'center',
      }] : []),
      ...((canUseCouponRecord.value || bobProPageRef.value?.tableData?.some(r => isThirdPartyCoupon(r.Coupon?.type))) ? [{
        prop: 'operate',
        label: i18n.t('member.common.action.text'),
        fixed: 'right',
        width: 180,
        component: OperateColumn,
        props: (row) => {
          const items = []
          const type = getCouponRecordType(row)

          // 核銷按鈕（T3.5：需 couponRecord.use 權限，pospal 由外部系統核銷）
          if (type !== 'pospal' && canUseCouponRecord.value) {
            items.push({
              key: 'use',
              component: 'BaseElButton',
              render: () => i18n.t('member.tab.couponHistory.redeem'),
              props: {
                type: 'text',
                class: ['underline', 'text-action'],
                disabled: disableUseCoupon(row),
              },
              on: {
                click: () => {
                  selectCoupon.value = row
                  showUseCouponDialog.value = true
                },
              },
            })
          }

          // 標記作廢按鈕（T3.6：markVoid 兩群組皆有，不需額外閘）
          if (isThirdPartyCoupon(type)) {
            items.push({
              key: 'void',
              component: 'BaseElButton',
              render: () => i18n.t('member.tab.couponHistory.markVoid'),
              props: {
                type: 'text',
                class: ['underline', 'text-action'],
                disabled: !canVoidCoupon(type, row.status),
              },
              on: {
                click: () => {
                  selectVoidRow.value = row
                  voidDialog.value = true
                },
              },
            })
          }

          return { items }
        },
        align: 'center',
      }] : []),
    ])

    // 表格配置
    const tableConfig = reactive({
      pagination: {
        enabled: true,
        pageLimit: 10,
        pageSizes: [10, 20, 50],
      },
      apis: {
        get: GetCouponRecord,
        count: GetCouponRecordCount,
        getPayload: (filters) => ({
          MemberId: props.memberId,
          status: filters.status || undefined,
          sort: filters.orderBy || undefined,
        }),
      },
      filters: [
        {
          key: 'status',
          component: 'BaseElSelect',
          props: () => ({
            clearable: true,
            placeholder: i18n.t('member.tab.couponHistory.usageStatus'),
          }),
        },
        {
          key: 'orderBy',
          component: 'BaseElSelect',
          props: () => ({
            clearable: true,
            placeholder: i18n.t('member.tab.couponHistory.sortBy'),
          }),
        },
      ],
    })

    // 方法
    const loadCouponResources = async () => {
      await getAllCouponResources()
      couponList.value = composeAllCouponResources.value
    }

    const findCouponName = (couponId) => {
      if (!couponId) return ''
      const coupon = find(couponList.value, { id: couponId })
      return coupon ? coupon.name : ''
    }

    const onAddCoupon = (ids) => {
      if (ids && ids.length > 0) {
        formData.couponId = ids[0]
      }
      showCouponSelectModal.value = false
    }

    const clearForm = () => {
      formData.couponId = null
      formData.message = ''
      formUtils.resetForm(addCouponForm.value)
      couponWarnings.value = []
    }

    const checkCouponCreateWarnings = async (couponId) => {
      if (!couponId) {
        couponWarnings.value = []
        return
      }

      if (loading.checkCouponNotify) {
        return
      }

      try {
        loading.checkCouponNotify = true
        const [res, err] = await GetCouponRecordCreateCheck({
          shopId: shopId.value,
          couponId,
        })

        if (err) {
          window.$message.error(err || i18n.t('common.message.unknownError'))
          couponWarnings.value = []
          return
        }

        couponWarnings.value = res?.warnings || []
      } finally {
        loading.checkCouponNotify = false
      }
    }
    const createCouponRecord = async (e, force = false) => {
      if (!(await formUtils.checkForm(addCouponForm.value))) return

      if (!force) await checkCouponCreateWarnings(formData.couponId)

      if (!force && !couponNotify.value) {
        showWarningDialog.value = true
        return
      }

      loading.addCoupon = true
      try {
        const [, err] = await CreateCouponRecord({
          shopId: shopId.value,
          memberId: props.memberId,
          couponId: formData.couponId,
          message: formData.message || undefined,
        })

        if (err) {
          window.$message.error(i18n.t('common.message.createFail', { message: err || i18n.t('common.message.unknownError') }))
          return
        }

        window.$message.success(i18n.t('common.message.createSuccess2'))
        clearForm()
        onRefresh()
        couponWarnings.value = []
      } finally {
        loading.addCoupon = false
        showWarningDialog.value = false
      }
    }

    const useCoupon = async () => {
      try {
        loading.useCoupon = true
        const [, err] = await UseCoupon({
          shopId: shopId.value,
          id: get(selectCoupon.value, 'id'),
        })
        if (err) {
          window.$message.error(err.message || i18n.t('member.tab.couponHistory.redeemFailed'))
          return
        }
        window.$message.success(i18n.t('member.tab.couponHistory.redeemSuccess'))
        showUseCouponDialog.value = false
        onRefresh()
      } catch (error) {
        window.$message.error(error.message || '核銷失敗')
      } finally {
        loading.useCoupon = false
      }
    }

    const voidCouponRecord = async () => {
      try {
        loading.voidCoupon = true
        const [, err] = await MarkVoidCouponRecord({
          shopId: shopId.value,
          id: get(selectVoidRow.value, 'id'),
        })
        if (err) {
          window.$message.error(err || i18n.t('member.tab.couponHistory.markVoidFailed'))
          return
        }
        window.$message.success(i18n.t('member.tab.couponHistory.markVoidSuccess'))
        voidDialog.value = false
        onRefresh()
      } catch (error) {
        window.$message.error(error.message || i18n.t('member.tab.couponHistory.markVoidFailed'))
      } finally {
        loading.voidCoupon = false
      }
    }

    onMounted(async () => {
      await loadCouponResources()
    })

    return {
      // Refs
      bobProPageRef,
      addCouponForm,
      couponList,
      showCouponSelectModal,
      showWarningDialog,
      showUseCouponDialog,
      voidDialog,
      loading,
      selectCoupon,
      selectVoidRow,
      couponWarnings,

      // Computed
      canCreateCouponRecord,
      couponTypeOptions,
      tableColumns,
      couponNotify,

      // Reactive
      formData,
      formRules,
      tableConfig,

      // Config
      couponStatusConfig,
      orderByOptionsConfig,

      // Methods
      findCouponName,
      onAddCoupon,
      clearForm,
      createCouponRecord,
      useCoupon,
      voidCouponRecord,
      isThirdPartyCoupon,
      canVoidCoupon,
      hideUseCoupon,
      disableUseCoupon,
      formatDate,
      couponType,
      transOriginType,
    }
  },
})
</script>

<style lang="postcss" scoped></style>
